import { useEffect, useRef } from 'react';
import maplibregl from 'maplibre-gl';

interface UseMapStyleControlOptions {
  map: maplibregl.Map | null;
  currentStyle: string;
  onStyleChange: (style: string) => void;
  is3D?: boolean;
}

export const useMapStyleControl = ({
  map,
  currentStyle,
  onStyleChange,
  is3D = false
}: UseMapStyleControlOptions) => {
  const controlRef = useRef<any>(null);
  const containerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (!map || is3D) {
      // Remove control if not needed or in 3D mode
      if (controlRef.current) {
        try {
          map?.removeControl(controlRef.current);
        } catch (e) {
          // Control might already be removed
        }
        controlRef.current = null;
      }
      return;
    }

    // Create custom map style control
    class MapStyleControl {
      container!: HTMLDivElement;
      expanded: boolean = false;

      onAdd(_map: maplibregl.Map) {
        this.container = document.createElement('div');
        this.container.className = 'maplibregl-ctrl maplibregl-ctrl-group map-style-control-icon';

        const styles = [
          { id: 'osm', name: 'OpenStreetMap' },  // Free option first
          { id: 'topo', name: 'Topographic' },
          { id: 'satellite', name: 'Satellite' },
          { id: 'streets', name: 'Streets' }
        ];

        // Icon button
        const iconButton = document.createElement('button');
        iconButton.className = 'map-style-icon-btn';
        iconButton.innerHTML = '🏔️';
        iconButton.title = 'Map Styles';

        // Options dropdown
        const optionsDropdown = document.createElement('div');
        optionsDropdown.className = 'map-style-dropdown-premium';
        optionsDropdown.style.display = 'none';

        // Create option buttons
        styles.forEach(style => {
          const option = document.createElement('div');
          option.className = `map-style-option-premium${currentStyle === style.id ? ' active' : ''}`;
          option.innerHTML = `<span class="option-name">${style.name}</span>`;

          option.addEventListener('click', (e) => {
            e.stopPropagation();
            onStyleChange(style.id);
            // Update active state
            optionsDropdown.querySelectorAll('.map-style-option-premium').forEach(o =>
              o.classList.remove('active')
            );
            option.classList.add('active');
            // Collapse dropdown
            optionsDropdown.style.display = 'none';
            this.expanded = false;
          });

          optionsDropdown.appendChild(option);
        });

        // Toggle dropdown
        iconButton.addEventListener('click', (e) => {
          e.stopPropagation();
          this.expanded = !this.expanded;
          optionsDropdown.style.display = this.expanded ? 'block' : 'none';
        });

        // Close dropdown when clicking outside
        document.addEventListener('click', () => {
          if (this.expanded) {
            optionsDropdown.style.display = 'none';
            this.expanded = false;
          }
        });

        this.container.appendChild(iconButton);
        this.container.appendChild(optionsDropdown);
        containerRef.current = this.container;
        return this.container;
      }

      onRemove() {
        if (containerRef.current?.parentNode) {
          containerRef.current.parentNode.removeChild(containerRef.current);
        }
      }
    }

    // Remove existing control if it exists
    if (controlRef.current) {
      map.removeControl(controlRef.current);
    }

    // Add new control
    const control = new MapStyleControl();
    controlRef.current = control;
    map.addControl(control as any, 'top-right');

    return () => {
      if (map && controlRef.current) {
        try {
          map.removeControl(controlRef.current);
        } catch (e) {
          // Map might be destroyed
        }
      }
    };
  }, [map, currentStyle, onStyleChange, is3D]);
};